/**
 * Countdown Timer Block
 * Live countdown to a target date with optional CTA.
 */

import { registerBlockRenderer } from '../registry';
import { escapeHtml, createBlockWrapper } from '../utils';

// =============================================================================
// TYPES
// =============================================================================

interface CountdownData {
  targetDate: string;
  title?: string;
  expiredMessage?: string;
  showSeconds?: boolean;
  action?: {
    label: string;
    value: string;
  };
}

// =============================================================================
// TYPE VALIDATION
// =============================================================================

function isValidCountdownData(data: unknown): data is CountdownData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  // Must have targetDate string
  if (typeof d.targetDate !== 'string') return false;

  // Validate it's a parseable date
  const date = new Date(d.targetDate);
  if (isNaN(date.getTime())) return false;

  return true;
}

// =============================================================================
// RENDER FUNCTION
// =============================================================================

function renderCountdown(
  data: CountdownData,
  container: HTMLElement,
  onAction?: (value: string) => void
): void {
  const wrapper = createBlockWrapper('countdown');

  const targetDate = new Date(data.targetDate).getTime();
  const showSeconds = data.showSeconds !== false;

  let html = '';
  if (data.title) {
    html += `<h3 class="pms-countdown-title">${escapeHtml(data.title)}</h3>`;
  }

  html += `
    <div class="pms-countdown">
      <div class="pms-countdown-timer">
        <div class="pms-countdown-unit">
          <span class="pms-countdown-value" data-display="days">00</span>
          <span class="pms-countdown-label">Days</span>
        </div>
        <div class="pms-countdown-separator">:</div>
        <div class="pms-countdown-unit">
          <span class="pms-countdown-value" data-display="hours">00</span>
          <span class="pms-countdown-label">Hours</span>
        </div>
        <div class="pms-countdown-separator">:</div>
        <div class="pms-countdown-unit">
          <span class="pms-countdown-value" data-display="minutes">00</span>
          <span class="pms-countdown-label">Minutes</span>
        </div>
        ${showSeconds ? `
          <div class="pms-countdown-separator">:</div>
          <div class="pms-countdown-unit">
            <span class="pms-countdown-value" data-display="seconds">00</span>
            <span class="pms-countdown-label">Seconds</span>
          </div>
        ` : ''}
      </div>
    </div>
    <div class="pms-countdown-expired" data-display="expired" style="display: none;">
      ${escapeHtml(data.expiredMessage || 'Event has started!')}
    </div>
  `;

  // Optional action button
  if (data.action) {
    html += `
      <div class="pms-countdown-action">
        <button class="pms-countdown-btn" data-action="${escapeHtml(data.action.value)}">
          ${escapeHtml(data.action.label)}
        </button>
      </div>
    `;
  }

  wrapper.innerHTML = html;

  // Get elements
  const daysEl = wrapper.querySelector('[data-display="days"]') as HTMLElement;
  const hoursEl = wrapper.querySelector('[data-display="hours"]') as HTMLElement;
  const minutesEl = wrapper.querySelector('[data-display="minutes"]') as HTMLElement;
  const secondsEl = wrapper.querySelector('[data-display="seconds"]') as HTMLElement | null;
  const timerEl = wrapper.querySelector('.pms-countdown-timer') as HTMLElement;
  const expiredEl = wrapper.querySelector('[data-display="expired"]') as HTMLElement;

  function updateCountdown(): void {
    const now = Date.now();
    const diff = targetDate - now;

    if (diff <= 0) {
      timerEl.style.display = 'none';
      expiredEl.style.display = 'block';
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    daysEl.textContent = String(days).padStart(2, '0');
    hoursEl.textContent = String(hours).padStart(2, '0');
    minutesEl.textContent = String(minutes).padStart(2, '0');
    if (secondsEl) {
      secondsEl.textContent = String(seconds).padStart(2, '0');
    }
  }

  // Initial update
  updateCountdown();

  // Start interval
  const interval = setInterval(updateCountdown, 1000);

  // Cleanup when element is removed from DOM
  const observer = new MutationObserver(() => {
    if (!document.contains(wrapper)) {
      clearInterval(interval);
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Action button handler
  if (onAction && data.action) {
    const btn = wrapper.querySelector('[data-action]') as HTMLElement;
    btn?.addEventListener('click', () => {
      onAction(data.action!.value);
    });
  }

  container.appendChild(wrapper);
}

// =============================================================================
// REGISTRATION
// =============================================================================

registerBlockRenderer('countdown', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  if (!isValidCountdownData(data)) {
    console.warn('[countdown] Invalid data:', data);
    const wrapper = createBlockWrapper('countdown');
    wrapper.innerHTML = '<div class="pms-block-error">Invalid countdown data - requires valid targetDate</div>';
    container.appendChild(wrapper);
    return;
  }

  renderCountdown(data, container, onAction);
});
